import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiFetch, supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, ClipboardList, Trash2, Search, Edit, Eye, CheckCircle2, RotateCcw, X, ExternalLink, Download } from "lucide-react";
import { PageHeader, StatusBadge, StatusTabs, EmptyState, formatDate } from "@/lib/ui";
import { Typeahead } from "@/components/Typeahead";
import { downloadCsv, iso } from "@/lib/csvExport";

const SERVICE_OPTS = [
  "Administrative Support", "Virtual Assistant Services", "Calendar Management",
  "Scheduling", "Email Management", "Data Entry", "Document Management",
  "Research", "Customer Support", "Project Coordination", "CRM Support",
  "Intake Coordination", "Other",
];
const STATUSES = ["draft", "submitted", "in_review", "assigned", "in_progress", "completed", "cancelled"];

const empty = () => ({
  client_id: "", client: null, title: "", services_requested: [], description: "",
  desired_outcome: "", priority: "normal", requested_due_date: "", recurring: false,
  additional_instructions: "", assigned_to: "", assignee: null, staff_role: "", status: "draft",
});

export default function ServiceRequests() {
  const { currentOrgId } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);       // for Client picker in create/edit dialog
  const [detailSR, setDetailSR] = useState(null);   // ← currently-open detail drawer

  // Sync filters/search to URL so Back-nav restores exact view.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    if (statusFilter && statusFilter !== "all") next.set("status", statusFilter); else next.delete("status");
    setParams(next, { replace: true });
    /* eslint-disable-next-line */
  }, [q, statusFilter]);

  const load = async () => {
    setLoading(true);
    // Three independent fetches — a failure in one must never blank the others.
    const [reqs, staffList, clientList] = await Promise.allSettled([
      apiFetch(`/service-requests?organization_id=${currentOrgId}`),
      apiFetch(`/scheduling/staff?organization_id=${currentOrgId}`),
      supabase.from("clients").select("id,first_name,last_name,email,phone,referral_source")
        .eq("organization_id", currentOrgId).order("first_name"),
    ]);
    let fresh = [];
    if (reqs.status === "fulfilled") { fresh = reqs.value || []; setRows(fresh); }
    else toast.error(`Could not load requests: ${reqs.reason?.message || "unknown error"}`);
    if (staffList.status === "fulfilled") {
      setStaff((staffList.value?.staff || []).map((m) => ({
        id: m.user_id, full_name: m.full_name, email: m.email, role: m.role,
      })));
    }
    if (clientList.status === "fulfilled") {
      setClients(clientList.value?.data || []);
    }
    // Refresh the open drawer if the record still exists after reload.
    if (detailSR) {
      const updated = fresh.find((r) => r.id === detailSR.id);
      if (updated) setDetailSR(updated);
    }
    setLoading(false);
  };
  useEffect(() => { if (currentOrgId) load(); /* eslint-disable-next-line */ }, [currentOrgId]);

  // Deep-link: open ?id=xxx row automatically once rows load
  useEffect(() => {
    const id = params.get("id");
    if (!id || !rows.length || detailSR) return;
    const row = rows.find((r) => r.id === id);
    if (row) { setDetailSR(row); params.delete("id"); setParams(params, { replace: true }); }
    /* eslint-disable-next-line */
  }, [rows, params.get("id")]);

  const openNew = () => { setEditing(null); setForm(empty()); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      client_id: r.client_id, client: r.clients ? { id: r.client_id, first_name: r.clients.first_name, last_name: r.clients.last_name, email: r.clients.email } : null,
      title: r.title || "", services_requested: r.services_requested || [], description: r.description || "",
      desired_outcome: r.desired_outcome || "", priority: r.priority || "normal",
      requested_due_date: r.requested_due_date || "", recurring: !!r.recurring,
      additional_instructions: r.additional_instructions || "", assigned_to: r.assigned_to || "",
      assignee: r.assigned_to ? staff.find((s) => s.id === r.assigned_to) : null,
      staff_role: r.staff_role || "", status: r.status || "draft",
    });
    setDialogOpen(true);
  };

  const save = async (statusOverride) => {
    if (!form.client_id) return toast.error("Please select a client");
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.description.trim()) return toast.error("Description is required");
    if ((form.services_requested || []).length === 0) return toast.error("Select at least one service");
    const payload = {
      organization_id: currentOrgId,
      client_id: form.client_id,
      title: form.title.trim(),
      services_requested: form.services_requested,
      description: form.description.trim(),
      desired_outcome: form.desired_outcome || null,
      priority: form.priority,
      requested_due_date: form.requested_due_date || null,
      recurring: form.recurring,
      additional_instructions: form.additional_instructions || null,
      assigned_to: form.assigned_to || null,
      staff_role: form.staff_role || null,
      status: statusOverride || form.status,
    };
    try {
      if (editing) {
        const updated = await apiFetch(`/service-requests/${editing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
        // Silent RLS block guard — API returns null / falsy row if blocked.
        if (updated && updated.id) {
          toast.success("Service request updated");
          if (detailSR && detailSR.id === editing.id) setDetailSR({ ...detailSR, ...updated, clients: detailSR.clients, profiles: detailSR.profiles });
        } else {
          toast.error("Update was blocked. You may not have permission to change this record.");
        }
      } else {
        await apiFetch(`/service-requests`, { method: "POST", body: JSON.stringify(payload) });
        toast.success(payload.status === "submitted" ? "Request submitted & task assigned" : "Draft saved");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.message || "Save failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete service request "${r.title}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/service-requests/${r.id}`, { method: "DELETE" });
      toast.success("Deleted");
      setDetailSR(null); setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.message); }
  };

  // Quick status change from drawer — uses apiFetch (RLS-safe: backend surfaces 4xx).
  const setSRStatus = async (r, newStatus) => {
    try {
      const updated = await apiFetch(`/service-requests/${r.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      if (!updated || !updated.id) return toast.error("Update was blocked. You may not have permission.");
      toast.success(`Status changed to ${newStatus.replace(/_/g, " ")}`);
      setDetailSR({ ...r, ...updated, clients: r.clients, profiles: r.profiles });
      load();
    } catch (e) { toast.error(e.message || "Update failed"); }
  };

  const toggleService = (s) => setForm((f) => ({
    ...f, services_requested: f.services_requested.includes(s)
      ? f.services_requested.filter((x) => x !== s) : [...f.services_requested, s]
  }));

  const filtered = rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (q && !`${r.title} ${r.clients?.first_name} ${r.clients?.last_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const STATUS_TABS = [
    { id: "all",         label: "All" },
    { id: "draft",       label: "Draft" },
    { id: "submitted",   label: "Submitted" },
    { id: "in_review",   label: "In Review" },
    { id: "assigned",    label: "Assigned" },
    { id: "in_progress", label: "In Progress" },
    { id: "completed",   label: "Completed" },
    { id: "cancelled",   label: "Cancelled" },
  ];
  const statusCounts = (() => {
    const c = { all: rows.length };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  })();

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="service-requests-page">
      <PageHeader title="Service Requests" subtitle="Track service requests, assign staff, and turn them into tasks."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" data-testid="sr-export-csv"
              disabled={filtered.length === 0}
              onClick={() => downloadCsv(
                `service-requests-${iso(new Date())}`,
                [
                  { key: "title", label: "Title" },
                  { label: "Client", get: (r) => r.clients ? `${r.clients.first_name} ${r.clients.last_name}` : "" },
                  { key: "status", label: "Status" },
                  { key: "priority", label: "Priority" },
                  { label: "Services", get: (r) => (r.services_requested || []).join("; ") },
                  { label: "Description", get: (r) => r.description || "" },
                  { label: "Desired outcome", get: (r) => r.desired_outcome || "" },
                  { label: "Due date", get: (r) => iso(r.requested_due_date) },
                  { label: "Assignee", get: (r) => r.profiles?.full_name || "" },
                  { label: "Staff role", get: (r) => r.staff_role || "" },
                  { label: "Recurring", get: (r) => r.recurring ? "Yes" : "No" },
                  { label: "Created", get: (r) => iso(r.created_at) },
                  { label: "Updated", get: (r) => iso(r.updated_at) },
                ],
                filtered,
              )}
            ><Download size={14} className="mr-1.5" /> Export CSV</Button>
            <Button data-testid="new-svc-req-btn" onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Plus size={14} className="mr-1.5" /> New request</Button>
          </div>
        }
      />
      {rows.length > 0 && (
        <StatusTabs
          tabs={STATUS_TABS}
          value={statusFilter}
          onChange={setStatusFilter}
          counts={statusCounts}
          testidPrefix="sr-status-tab"
          right={
            <div className="relative hidden md:block">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
              <input
                data-testid="svc-req-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search title or client…"
                className="bg-transparent border-0 outline-none text-sm pl-9 pr-3 min-w-[240px]"
              />
            </div>
          }
        />
      )}

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No service requests yet"
          description="Create your first service request. Once submitted with an assignee it will auto-create a task."
          action={<Button onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Plus size={14} className="mr-1.5" /> New request</Button>} />
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
          {/* Desktop table (≥md) */}
          <table className="w-full text-sm hidden md:table">
            <thead className="bg-[#FAF8F5] text-xs uppercase tracking-widest text-[#57534E]">
              <tr>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">Due</th>
                <th className="text-left px-4 py-3">Assignee</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E2D9]">
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`sr-row-${r.id}`} onClick={() => setDetailSR(r)} className="hover:bg-[#FAF8F5] cursor-pointer">
                  <td className="px-4 py-3 font-medium">{r.title}</td>
                  <td className="px-4 py-3">
                    {r.clients ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/app/clients/${r.client_id}`); }}
                        className="text-[#B85C1F] hover:underline"
                        data-testid={`sr-client-link-${r.id}`}
                      >
                        {r.clients.first_name} {r.clients.last_name}
                      </button>
                    ) : <span className="text-[#57534E]">—</span>}
                  </td>
                  <td className="px-4 py-3 capitalize">{r.priority}</td>
                  <td className="px-4 py-3 text-[#57534E]">{r.requested_due_date ? formatDate(r.requested_due_date) : "—"}</td>
                  <td className="px-4 py-3 text-[#57534E]">{r.profiles?.full_name || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge value={r.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`sr-view-${r.id}`}
                      onClick={(e) => { e.stopPropagation(); setDetailSR(r); }}
                      className="text-xs px-2 py-1 rounded-full text-[#57534E] hover:bg-[#FAF8F5] inline-flex items-center gap-1"
                    >
                      <Eye size={12} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile card list (<md) */}
          <div className="md:hidden divide-y divide-[#E8E2D9]">
            {filtered.map((r) => (
              <button
                key={r.id}
                onClick={() => setDetailSR(r)}
                className="w-full text-left px-4 py-4 hover:bg-[#FAF8F5] active:bg-[#FDE9D8]/40 transition-colors"
                data-testid={`sr-card-${r.id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold min-w-0 truncate">{r.title}</div>
                  <StatusBadge value={r.status} />
                </div>
                {r.clients && (
                  <div className="text-sm text-[#57534E] truncate mt-0.5">{r.clients.first_name} {r.clients.last_name}</div>
                )}
                <div className="text-xs text-[#A8A29E] mt-1 flex flex-wrap gap-x-2">
                  <span className="capitalize">{r.priority || "normal"}</span>
                  {r.requested_due_date && <span>· Due {formatDate(r.requested_due_date)}</span>}
                  {r.profiles?.full_name && <span>· {r.profiles.full_name}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── Service Request Detail Drawer ──────────────────────────────── */}
      <Sheet open={!!detailSR} onOpenChange={(v) => { if (!v) setDetailSR(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="sr-detail-drawer">
          {detailSR && (() => {
            const r = detailSR;
            const isCompleted = r.status === "completed";
            const isCancelled = r.status === "cancelled";
            return (
              <>
                <SheetHeader className="pr-8">
                  <SheetTitle className="font-display tracking-tight text-2xl leading-tight" data-testid="sr-drawer-title">{r.title}</SheetTitle>
                  <SheetDescription asChild>
                    <div className="flex gap-2 flex-wrap items-center">
                      <StatusBadge value={r.status} testid="sr-drawer-status" />
                      <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FDE9D8] text-[#B85C1F] capitalize">
                        {r.priority || "normal"} priority
                      </span>
                    </div>
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-5">
                  {r.description && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1.5">Description</div>
                      <div className="text-sm whitespace-pre-wrap" data-testid="sr-drawer-description">{r.description}</div>
                    </div>
                  )}

                  {r.desired_outcome && (
                    <div className="pt-3 border-t border-[#E8E2D9]">
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1.5">Desired outcome</div>
                      <div className="text-sm whitespace-pre-wrap" data-testid="sr-drawer-outcome">{r.desired_outcome}</div>
                    </div>
                  )}

                  {(r.services_requested || []).length > 0 && (
                    <div className="pt-3 border-t border-[#E8E2D9]">
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1.5">Services requested</div>
                      <div className="flex flex-wrap gap-1.5" data-testid="sr-drawer-services">
                        {r.services_requested.map((s) => (
                          <span key={s} className="text-xs px-2 py-1 rounded-full bg-[#FAF8F5] border border-[#E8E2D9]">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[#E8E2D9]">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Client</div>
                      <div className="text-sm" data-testid="sr-drawer-client">
                        {r.clients ? (
                          <button
                            onClick={() => { navigate(`/app/clients/${r.client_id}`); setDetailSR(null); }}
                            className="text-[#B85C1F] hover:underline"
                          >
                            {r.clients.first_name} {r.clients.last_name}
                          </button>
                        ) : <span className="text-[#A8A29E]">—</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Assignee</div>
                      <div className="text-sm" data-testid="sr-drawer-assignee">
                        {r.profiles?.full_name || <span className="text-[#A8A29E]">Unassigned</span>}
                        {r.staff_role && <div className="text-xs text-[#A8A29E]">{r.staff_role}</div>}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Due date</div>
                      <div className="text-sm" data-testid="sr-drawer-due">{r.requested_due_date ? formatDate(r.requested_due_date) : <span className="text-[#A8A29E]">—</span>}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Status</div>
                      <Select value={r.status} onValueChange={(v) => setSRStatus(r, v)}>
                        <SelectTrigger className="h-8 text-sm capitalize" data-testid="sr-drawer-status-select">
                          <SelectValue>{(r.status || "").replace(/_/g, " ")}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  {r.additional_instructions && (
                    <div className="pt-3 border-t border-[#E8E2D9]">
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1.5">Additional instructions</div>
                      <div className="text-sm whitespace-pre-wrap" data-testid="sr-drawer-notes">{r.additional_instructions}</div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[#E8E2D9]">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Recurring</div>
                      <div className="text-sm">{r.recurring ? "Yes" : "No"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Created</div>
                      <div className="text-sm text-[#57534E]" data-testid="sr-drawer-created">{r.created_at ? formatDate(r.created_at) : "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Last updated</div>
                      <div className="text-sm text-[#57534E]" data-testid="sr-drawer-updated">{r.updated_at ? formatDate(r.updated_at) : "—"}</div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-[#E8E2D9] flex items-center gap-2 flex-wrap">
                    <Button data-testid="sr-drawer-edit-btn" onClick={() => { openEdit(r); }} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
                      <Edit size={14} className="mr-1.5" /> Edit
                    </Button>
                    {!isCompleted && !isCancelled && (
                      <Button data-testid="sr-drawer-complete-btn" onClick={() => setSRStatus(r, "completed")} variant="outline" className="rounded-full text-emerald-700 border-emerald-200">
                        <CheckCircle2 size={14} className="mr-1.5" /> Mark complete
                      </Button>
                    )}
                    {isCompleted && (
                      <Button data-testid="sr-drawer-reopen-btn" onClick={() => setSRStatus(r, "in_progress")} variant="outline" className="rounded-full">
                        <RotateCcw size={14} className="mr-1.5" /> Reopen
                      </Button>
                    )}
                    {r.client_id && (
                      <Button
                        data-testid="sr-drawer-open-profile-btn"
                        onClick={() => { navigate(`/app/clients/${r.client_id}`); setDetailSR(null); }}
                        variant="outline" className="rounded-full"
                      >
                        <ExternalLink size={13} className="mr-1.5" /> Open client
                      </Button>
                    )}
                    <Button data-testid="sr-drawer-delete-btn" onClick={() => remove(r)} variant="outline" className="rounded-full text-red-600 border-red-200">
                      <Trash2 size={14} className="mr-1.5" /> Delete
                    </Button>
                    <Button data-testid="sr-drawer-close-btn" onClick={() => setDetailSR(null)} variant="ghost" className="rounded-full ml-auto">
                      <X size={14} className="mr-1" /> Close
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      {/* ─── Create / Edit Dialog (unchanged) ───────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit service request" : "New service request"}</DialogTitle>
            <DialogDescription className="text-xs text-[#57534E]">Fill out the details. When submitted with an assigned staff member, a task is created automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <SectionCard title="Client information">
              <Label className="text-sm">Client *</Label>
              <Typeahead
                options={clients.map((c) => ({
                  value: c.id,
                  label: `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Unnamed client",
                  sub: c.email || c.phone || undefined,
                }))}
                value={form.client_id}
                onChange={(id) => {
                  const picked = clients.find((c) => c.id === id) || null;
                  setForm({ ...form, client_id: id || "", client: picked });
                }}
                placeholder="Search clients…" testid="sr-client" />
              {form.client && (
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs bg-[#FAF8F5] rounded-lg p-3">
                  <div><span className="text-[#A8A29E]">Company/Org:</span> {form.client.referral_source || "—"}</div>
                  <div><span className="text-[#A8A29E]">Primary contact:</span> {form.client.first_name} {form.client.last_name}</div>
                  <div className="col-span-2"><span className="text-[#A8A29E]">Email:</span> {form.client.email || "—"}</div>
                </div>
              )}
            </SectionCard>

            <SectionCard title="Service Request">
              <Field label="Request title" required><Input data-testid="sr-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
              <Field label="Service requested" required>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                  {SERVICE_OPTS.map((s) => (
                    <label key={s} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${form.services_requested.includes(s) ? "bg-[#FDE9D8] border-[#F0C69A]" : "bg-white border-[#E8E2D9]"}`}>
                      <input type="checkbox" checked={form.services_requested.includes(s)} onChange={() => toggleService(s)} className="accent-[#E8712C]" data-testid={`sr-svc-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />{s}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Description" required><Textarea rows={3} data-testid="sr-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
              <Field label="Desired outcome"><Textarea rows={2} data-testid="sr-outcome" value={form.desired_outcome} onChange={(e) => setForm({ ...form, desired_outcome: e.target.value })} /></Field>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Priority *">
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="sr-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Requested due date"><Input type="date" data-testid="sr-due" value={form.requested_due_date} onChange={(e) => setForm({ ...form, requested_due_date: e.target.value })} /></Field>
                <Field label="Recurring">
                  <Select value={form.recurring ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, recurring: v === "yes" })}>
                    <SelectTrigger data-testid="sr-recurring"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Additional instructions"><Textarea rows={2} data-testid="sr-notes" value={form.additional_instructions} onChange={(e) => setForm({ ...form, additional_instructions: e.target.value })} /></Field>
            </SectionCard>

            <SectionCard title="Assignment">
              <Field label="Assigned staff">
                <Typeahead
                  options={(staff || []).map((s) => ({
                    value: s.id,
                    label: s.full_name || s.email || "Unnamed staff",
                    sub: s.email && s.email !== s.full_name ? s.email : undefined,
                  }))}
                  value={form.assigned_to}
                  onChange={(id) => {
                    const picked = (staff || []).find((s) => s.id === id);
                    const nextRole = form.staff_role || (picked?.role ? _roleLabel(picked.role) : "");
                    setForm({ ...form, assigned_to: id || "", assignee: picked || null, staff_role: nextRole });
                  }}
                  placeholder="Search staff by name or email…"
                  testid="sr-assignee" />
              </Field>
              <Field label="Staff role"><Input data-testid="sr-role" placeholder="e.g. Virtual Assistant, Project Manager" value={form.staff_role} onChange={(e) => setForm({ ...form, staff_role: e.target.value })} /></Field>
              {editing && (
                <Field label="Status">
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="sr-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              )}
            </SectionCard>
          </div>
          <div className="flex flex-wrap justify-end gap-2 pt-4 border-t border-[#E8E2D9]">
            {editing && (
              <Button
                data-testid="sr-dialog-delete-btn"
                variant="outline"
                onClick={() => remove(editing)}
                className="rounded-full text-red-600 border-red-200 mr-auto"
              >
                <Trash2 size={13} className="mr-1" /> Delete
              </Button>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="outline" className="rounded-full" onClick={() => save("draft")} data-testid="sr-save-draft">Save draft</Button>
            <Button className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]" onClick={() => save(editing ? undefined : "submitted")} data-testid="sr-submit">
              {editing ? "Save changes" : "Submit request"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Membership role → human-friendly Staff Role default.
function _roleLabel(r) {
  if (!r) return "";
  return ({
    org_admin: "Owner / Admin",
    admin: "Administrator",
    staff: "Virtual Assistant",
    va: "Virtual Assistant",
    manager: "Project Manager",
    provider: "Provider",
    coordinator: "Care Coordinator",
    intake: "Intake Coordinator",
  })[String(r).toLowerCase()] || String(r).replace(/_/g, " ");
}

function SectionCard({ title, children }) {
  return (
    <div className="border border-[#E8E2D9] rounded-2xl p-4">
      <div className="text-xs uppercase tracking-widest text-[#57534E] mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-sm">{label}{required && <span className="text-[#E8712C]"> *</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
